import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Shield } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Magic Link consumer — exchanges a short-lived `token` (sent via WhatsApp)
 * for a regular auth JWT, then redirects into the app.
 *
 * URL: /magic?token=eyJ...
 */
export default function MagicLink() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = params.get("token");
    if (!token) {
      setError("No magic token provided.");
      return;
    }
    api.get(`/auth/magic-link/verify?token=${encodeURIComponent(token)}`)
      .then((r) => {
        login(r.data.token, r.data.user);
        const next = params.get("next");
        const safeNext = next && next.startsWith("/app/") ? next : "/app/dashboard";
        nav(safeNext, { replace: true });
      })
      .catch((e) => {
        setError(e.response?.data?.detail || "This link is invalid or has expired.");
      });
  }, [params, login, nav]);

  return (
    <div className="min-h-dvh bg-gradient-to-br from-kudi-cream via-white to-accent flex items-center justify-center px-4">
      <div className="max-w-sm w-full bg-white border border-border rounded-3xl p-8 shadow-xl text-center">
        <div className="w-14 h-14 rounded-2xl bg-kudi-green/10 text-kudi-green flex items-center justify-center mx-auto mb-3">
          <Shield className="w-6 h-6" />
        </div>
        {!error ? (
          <>
            <h1 className="font-heading text-xl font-bold mb-1">Signing you in...</h1>
            <p className="text-sm text-kudi-ink/60 mb-4">One-tap login from WhatsApp.</p>
            <Loader2 className="w-6 h-6 mx-auto text-kudi-green animate-spin" />
          </>
        ) : (
          <>
            <h1 className="font-heading text-xl font-bold mb-1">Link expired</h1>
            <p className="text-sm text-kudi-ink/60 mb-4" data-testid="magic-error">{error}</p>
            <button
              onClick={() => nav("/signin")}
              className="bg-kudi-green text-white rounded-full px-6 py-2.5 text-sm font-semibold"
              data-testid="magic-retry"
            >
              Sign in with phone
            </button>
          </>
        )}
      </div>
    </div>
  );
}
